import { inject, Service, signal } from '@angular/core';
import { PasskeyCreationOptionsResponse, PasskeyOptionsResponse, PasskeyService } from './passkey.service';
import { NGXLogger } from 'ngx-logger';
import { WINDOW } from '../../providers/window/window.provider';
import { OAuthProvider } from '../../model/oauth-provider.enums';

type OAuthProviderObject = { enabled: boolean; provider: OAuthProvider; icon?: string };

@Service()
export class LoginPasskeyService {
  #passkeyService = inject(PasskeyService);
  #logger = inject(NGXLogger);
  #window = inject(WINDOW);

  providers: OAuthProviderObject[] = [
    { enabled: true, provider: OAuthProvider.Google, icon: 'google' },
    { enabled: true, provider: OAuthProvider.GitHub, icon: 'github' },
    { enabled: false, provider: OAuthProvider.Microsoft, icon: 'microsoft' },
  ];

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);

  handlePasskeyCreation(email: string, optionsResponse: PasskeyCreationOptionsResponse): void {
    const publicKey = this.#processCreationOptions(optionsResponse);

    this.#window.navigator.credentials
      .create({ publicKey })
      .then((credential) => {
        if (!credential) {
          throw new Error('Device failed to create passkey credential.');
        }
        const attestationResponse = this.#buildAttestationResponse(credential as PublicKeyCredential);
        this.#verifyRegistration(email, attestationResponse);
      })
      .catch((err: unknown) => {
        const error = err as Error;
        this.#logger.error('Passkey creation canceled or failed', error);
        this.errorMessage.set(error.message || 'Passkey creation was canceled or timed out.');
        this.isLoading.set(false);
      });
  }

  #verifyRegistration(email: string, attestationResponse: unknown): void {
    this.#passkeyService.verifyRegistration(email, attestationResponse).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.successMessage.set('Passkey successfully created and saved to account!');
      },
      error: (err) => {
        this.#logger.error('Passkey registration verification failed', err);
        this.errorMessage.set(err.error?.message || 'Passkey verification failed on backend.');
        this.isLoading.set(false);
      },
    });
  }

  #processCreationOptions(optionsResponse: PasskeyCreationOptionsResponse): PublicKeyCredentialCreationOptions {
    const publicKey: PublicKeyCredentialCreationOptions = optionsResponse.publicKeyCredentialCreationOptions;

    // Convert Base64URL strings to Uint8Array for browser WebAuthn API
    publicKey.challenge = this.#passkeyService.base64urlToUint8Array(publicKey.challenge as unknown as string);
    publicKey.user.id = this.#passkeyService.base64urlToUint8Array(publicKey.user.id as unknown as string);

    if (publicKey.excludeCredentials?.length) {
      for (const cred of publicKey.excludeCredentials) {
        cred.id = this.#passkeyService.base64urlToUint8Array(cred.id as unknown as string);
      }
    }

    return publicKey;
  }

  #buildAttestationResponse(credential: PublicKeyCredential): unknown {
    const response = credential.response as AuthenticatorAttestationResponse;

    return {
      id: credential.id,
      rawId: this.#passkeyService.bufferToBase64url(credential.rawId),
      type: credential.type,
      authenticatorAttachment: credential.authenticatorAttachment,
      response: {
        attestationObject: this.#passkeyService.bufferToBase64url(response.attestationObject),
        clientDataJSON: this.#passkeyService.bufferToBase64url(response.clientDataJSON),
        transports: response.getTransports ? response.getTransports() : [],
      },
    };
  }

  handlePasskeyLogin(email: string, options: PasskeyOptionsResponse): void {
    const publicKey = this.#processPublicKeyOptions(options);
    this.#window.navigator.credentials
      .get({ publicKey })
      .then((credential) => {
        if (!credential) {
          throw new Error('No credential returned from device.');
        }
        const assertionResponse = this.#buildAssertionResponse(credential as PublicKeyCredential);
        this.#verifyPasskeyAssertion(email, assertionResponse);
      })
      .catch((err: unknown) => {
        const error = err as Error;
        this.#logger.error('Passkey assertion failed', error);
        this.errorMessage.set(error.message || 'Passkey authentication failed. Please try again or use Magic Link.');
        this.isLoading.set(false);
      });
  }

  #verifyPasskeyAssertion(email: string, assertionResponse: unknown): void {
    this.#passkeyService.verifyAssertion(email, assertionResponse).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.successMessage.set('Successfully authenticated.');
        this.#window.location.reload();
      },
      error: (err) => {
        this.#logger.error('Passkey verification failed', err);
        this.errorMessage.set(err.error?.message || 'Verification failed. Please check your credentials.');
        this.isLoading.set(false);
      },
    });
  }

  #processPublicKeyOptions(options: PasskeyOptionsResponse): PublicKeyCredentialRequestOptions {
    const publicKey: PublicKeyCredentialRequestOptions = options.publicKeyCredentialRequestOptions;
    publicKey.challenge = this.#passkeyService.base64urlToUint8Array(publicKey.challenge as unknown as string);

    if (publicKey.allowCredentials?.length) {
      for (const cred of publicKey.allowCredentials) {
        cred.id = this.#passkeyService.base64urlToUint8Array(cred.id as unknown as string);
      }
    }
    return publicKey;
  }

  #buildAssertionResponse(credential: PublicKeyCredential): unknown {
    const response = credential.response as AuthenticatorAssertionResponse;
    return {
      id: credential.id,
      rawId: this.#passkeyService.bufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        authenticatorData: this.#passkeyService.bufferToBase64url(response.authenticatorData),
        clientDataJSON: this.#passkeyService.bufferToBase64url(response.clientDataJSON),
        signature: this.#passkeyService.bufferToBase64url(response.signature),
        userHandle: response.userHandle ? this.#passkeyService.bufferToBase64url(response.userHandle) : null,
      },
    };
  }
}
